import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const LAST_UPDATED = '2026-05-31';
const CONTACT_EMAIL = 'privacidade@saudenold.com.br';

const sectionsPt = [
  {
    title: '1. Quem somos',
    body: `Esta Política de Privacidade descreve como o **House Keep — Agenda & Management** ("House Keep", "nós") trata dados pessoais quando você utiliza o aplicativo web, o PWA instalável e o aplicativo Android (TWA) disponível na Google Play, acessíveis em **https://housekeep.saudenold.com.br**.

O controlador dos dados é a entidade responsável pela operação do serviço SaudeNold / House Keep, conforme cadastrada na conta de desenvolvedor Google Play e nos contratos com sua organização.`,
  },
  {
    title: '2. Dados que coletamos',
    body: `Podemos tratar as seguintes categorias de dados, conforme o uso do serviço pela sua empresa:

- **Conta e identificação:** nome, e-mail, telefone (opcional), documento (quando informado), papel de acesso (administrador, supervisor, funcionário, cliente).
- **Dados da organização:** empresa contratante, configurações multiempresa, permissões de menu e funcionalidades.
- **Operação e agenda:** agendamentos, clientes, propriedades, serviços, equipes, check-in/check-out, notas e histórico relacionados à gestão doméstica e de serviços.
- **Técnicos:** endereço IP, tipo de navegador/dispositivo, registros de acesso e erros para segurança e estabilidade.
- **Notificações:** preferências de notificação e, se você autorizar, token de push para lembretes de agendamento.

O House Keep **não** vende dados pessoais. Não utilizamos dados de saúde clínica, diagnóstico ou prontuário médico neste produto.`,
  },
  {
    title: '3. Finalidades e bases legais (LGPD)',
    body: `Utilizamos os dados para:

- autenticar usuários e manter sessões seguras;
- operar agendamentos, equipes e funcionalidades contratadas pela empresa;
- enviar notificações operacionais (quando habilitadas);
- cumprir obrigações legais e responder a solicitações de titulares;
- melhorar segurança, prevenir fraudes e corrigir falhas.

As bases legais incluem execução de contrato, legítimo interesse (segurança e melhoria do serviço) e consentimento quando exigido (por exemplo, notificações push).`,
  },
  {
    title: '4. Compartilhamento',
    body: `Os dados são acessíveis dentro da organização conforme permissões (RBAC). Podemos compartilhar dados com:

- **Provedores de infraestrutura** (hospedagem, banco de dados, e-mail transacional) sob contratos e medidas de segurança;
- **Autoridades**, quando exigido por lei.

Não compartilhamos dados com anunciantes. O aplicativo Android na Play Store **não exibe anúncios** na versão atual.`,
  },
  {
    title: '5. Retenção e segurança',
    body: `Mantemos os dados pelo tempo necessário à prestação do serviço, obrigações legais e resolução de disputas. Após exclusão da conta ou encerramento do contrato da empresa, aplicamos exclusão ou anonimização conforme política interna e prazos legais.

Aplicamos **criptografia em trânsito (HTTPS/TLS)** e controles de acesso. Senhas são armazenadas de forma irreversível (hash).`,
  },
  {
    title: '6. Seus direitos e exclusão de conta',
    body: `Nos termos da LGPD, você pode solicitar confirmação de tratamento, acesso, correção, portabilidade, anonimização, oposição ou exclusão de dados pessoais, além de revogar consentimentos quando aplicável.

**Exclusão de conta:** usuários vinculados a uma empresa devem solicitar exclusão ao administrador da organização. Titulares também podem contactar **${CONTACT_EMAIL}** com o assunto "Exclusão de conta House Keep", informando o e-mail cadastrado. Responderemos em prazo razoável, salvo retenção legal.`,
  },
  {
    title: '7. Crianças e público',
    body: `O House Keep é destinado a **uso profissional e organizacional (18+)**. Não é direcionado a crianças.`,
  },
  {
    title: '8. Alterações',
    body: `Podemos atualizar esta política. A data da última revisão consta no topo. Alterações relevantes podem ser comunicadas no aplicativo ou por e-mail aos administradores.`,
  },
  {
    title: '9. Contato',
    body: `Dúvidas ou solicitações sobre privacidade: **${CONTACT_EMAIL}**.

Site: https://housekeep.saudenold.com.br`,
  },
];

const sectionsEn = [
  {
    title: '1. Who we are',
    body: `This Privacy Policy explains how **House Keep — Agenda & Management** ("House Keep", "we") processes personal data when you use our web app, installable PWA, and Android app (TWA) on Google Play at **https://housekeep.saudenold.com.br**.`,
  },
  {
    title: '2. Data we collect',
    body: `Depending on your organization's use of the service, we may process: account identifiers (name, email, optional phone), company and role data, scheduling and operations data, technical logs (IP, device/browser), and push notification tokens if you opt in. We do not sell personal data and this product is not a clinical health records app.`,
  },
  {
    title: '3. Purposes',
    body: `We use data to authenticate users, deliver scheduling and management features, send operational notifications, ensure security, and comply with law.`,
  },
  {
    title: '4. Sharing',
    body: `Data is shared within your organization per role permissions and with infrastructure providers under safeguards. We do not share data with advertisers.`,
  },
  {
    title: '5. Security & retention',
    body: `We use HTTPS/TLS and access controls. Data is retained as needed for the service and legal obligations.`,
  },
  {
    title: '6. Your rights & account deletion',
    body: `You may exercise applicable privacy rights. For account deletion, contact your organization admin or email **${CONTACT_EMAIL}** with subject "House Keep account deletion" and your registered email.`,
  },
  {
    title: '7. Children',
    body: `The app is intended for professional use (18+), not children.`,
  },
  {
    title: '8. Changes & contact',
    body: `We may update this policy. Last updated: ${LAST_UPDATED}. Contact: **${CONTACT_EMAIL}**.`,
  },
];

const PrivacyPolicy: React.FC = () => {
  const { config } = useI18n();
  const isEn = config.language === 'en';
  const sections = isEn ? sectionsEn : sectionsPt;
  const title = isEn ? 'Privacy Policy' : 'Política de Privacidade';
  const backLabel = isEn ? 'Back to login' : 'Voltar ao login';
  const updatedLabel = isEn ? 'Last updated' : 'Última atualização';

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #6b21a8, #7c3aed, #a855f7)',
    padding: '1.5rem 1rem 3rem',
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: '48rem',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    padding: '2rem',
  };

  const renderMarkdownish = (text: string) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part.split('\n').map((line, j, arr) => (
        <React.Fragment key={`${i}-${j}`}>
          {line}
          {j < arr.length - 1 ? <br /> : null}
        </React.Fragment>
      ));
    });

  return (
    <div style={pageStyle}>
      <article style={cardStyle}>
        <header style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <Home style={{ height: '2rem', width: '2rem', color: '#9333ea' }} aria-hidden />
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>{title}</h1>
          </div>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>
            House Keep — Agenda & Management · {updatedLabel}: {LAST_UPDATED}
          </p>
        </header>

        {sections.map((section) => (
          <section key={section.title} style={{ marginBottom: '1.25rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>
              {section.title}
            </h2>
            <p style={{ color: '#4b5563', lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap' }}>
              {renderMarkdownish(section.body)}
            </p>
          </section>
        ))}

        <footer style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
          <Link
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#9333ea',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft size={18} aria-hidden />
            {backLabel}
          </Link>
        </footer>
      </article>
    </div>
  );
};

export default PrivacyPolicy;
